import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Build as BuildIcon,
  InfoOutlined as InfoIcon,
  Search as SearchIcon,
  ShoppingCart as CartIcon,
} from '@mui/icons-material';
import FloatingCart from '../components/FloatingCart';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useOrderNotification } from '../contexts/OrderNotificationContext';
import { mediaUrl, ordersApi, shopToolsApi, Tool } from '../services/api';
import { renderError } from '../utils/errorUtils';
import { parseTechnicalData, technicalDataPreview } from '../utils/toolTechnicalData';

export type ToolShopPageProps = {
  /** When true, render catalog only (no page chrome / FloatingCart) for embedding in /shop. */
  embedded?: boolean;
};

const ToolShopPage: React.FC<ToolShopPageProps> = ({ embedded = false }) => {
  const { isAuthenticated, user } = useAuth();
  const { addToolToCart, isToolInCart, getTotalItems } = useCart();
  const { refreshOrderCount } = useOrderNotification();

  const [tools, setTools] = useState<Tool[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [addingId, setAddingId] = useState<number | null>(null);
  const [detailTool, setDetailTool] = useState<Tool | null>(null);

  const loadTools = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await shopToolsApi.getAll({
        search: search || undefined,
        category: category !== 'all' ? category : undefined,
        limit: 48,
      });
      setTools(res.data.tools || []);
      setCategories(res.data.categories || []);
    } catch (err: any) {
      setError(renderError(err?.response?.data?.error || 'Failed to load tools'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadTools();
  };

  const handleAdd = async (tool: Tool) => {
    try {
      setAddingId(tool.tool_id);
      setError(null);
      if (!isAuthenticated) {
        setError('Please log in to add tools to your cart.');
        return;
      }
      addToolToCart({
        tool_id: tool.tool_id,
        tool_name: tool.tool_name,
        description: tool.description,
        category: tool.category,
        sell_price: Number(tool.sell_price) || 0,
        stock_quantity: Number(tool.stock_quantity) || 0,
        image_url: tool.image_url,
        tool_number: tool.tool_number,
      });
      setSuccess(`${tool.tool_name} added to cart`);
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setError(err?.message || 'Could not add tool to cart');
    } finally {
      setAddingId(null);
    }
  };

  const handleCheckout = async (orderData: any) => {
    const response = await ordersApi.create(orderData);
    if (user?.user_id) {
      try {
        await refreshOrderCount();
      } catch {
        /* ignore */
      }
    }
    return response;
  };

  return (
    <Box
      sx={
        embedded
          ? { width: '100%' }
          : { p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }
      }
    >
      {!embedded && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BuildIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h4" component="h1">
              Tool Shop
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Buy the tools you need to build and use MakerSets
            </Typography>
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSearch}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          placeholder="Search tools…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={category}
            onChange={(e) => setCategory(String(e.target.value))}
          >
            <MenuItem value="all">All categories</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button type="submit" variant="outlined">
          Search
        </Button>
        {!embedded && getTotalItems() > 0 && (
          <Chip icon={<CartIcon />} label={`${getTotalItems()} in cart`} color="primary" />
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : tools.length === 0 ? (
        <Alert severity="info">
          No tools for sale yet. Admins can mark tools as &quot;For sale&quot; in Tools Inventory
          and set a price and stock.
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr',
            },
            gap: 2,
          }}
        >
          {tools.map((tool) => {
            const price = Number(tool.sell_price) || 0;
            const stock = Number(tool.stock_quantity) || 0;
            const inCart = isToolInCart(tool.tool_id);
            const outOfStock = stock <= 0;
            const techPreview = technicalDataPreview(tool.technical_data, 2);
            return (
              <Card key={tool.tool_id} variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box
                  sx={{
                    height: 200,
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {tool.image_url ? (
                    <Box
                      component="img"
                      src={mediaUrl(tool.image_url)}
                      alt={tool.tool_name}
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <BuildIcon sx={{ fontSize: 56, color: 'grey.400' }} />
                  )}
                </Box>
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    {tool.tool_number || 'TOOL'}
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {tool.tool_name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                    {tool.category ? <Chip size="small" label={tool.category} /> : null}
                    {tool.tool_type ? (
                      <Chip size="small" variant="outlined" label={tool.tool_type} />
                    ) : null}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {(tool.description || '').slice(0, 120)}
                    {(tool.description || '').length > 120 ? '…' : ''}
                  </Typography>
                  {techPreview ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {techPreview}
                      {(parseTechnicalData(tool.technical_data).length > 2) ? '…' : ''}
                    </Typography>
                  ) : null}
                  <Typography variant="h6" color="primary">
                    €{price.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color={outOfStock ? 'error' : 'text.secondary'} display="block">
                    {outOfStock ? 'Out of stock' : `${stock} in stock`}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
                  <Button
                    fullWidth
                    variant={inCart ? 'outlined' : 'contained'}
                    startIcon={<CartIcon />}
                    disabled={outOfStock || addingId === tool.tool_id || price <= 0}
                    onClick={() => handleAdd(tool)}
                  >
                    {outOfStock ? 'Unavailable' : inCart ? 'Add another' : 'Add to cart'}
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    startIcon={<InfoIcon />}
                    onClick={() => setDetailTool(tool)}
                  >
                    Technical data
                  </Button>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      )}

      <Dialog
        open={Boolean(detailTool)}
        onClose={() => setDetailTool(null)}
        maxWidth="sm"
        fullWidth
      >
        {detailTool ? (
          <>
            <DialogTitle>{detailTool.tool_name}</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {detailTool.tool_number}
                {detailTool.category ? ` · ${detailTool.category}` : ''}
                {detailTool.tool_type ? ` · ${detailTool.tool_type}` : ''}
              </Typography>
              {detailTool.description ? (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {detailTool.description}
                </Typography>
              ) : null}

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Technical data
              </Typography>
              {parseTechnicalData(detailTool.technical_data).length > 0 ? (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableBody>
                    {parseTechnicalData(detailTool.technical_data).map((row, idx) => (
                      <TableRow key={`${row.label || 'row'}-${idx}`}>
                        {row.label ? (
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ width: '40%', color: 'text.secondary', border: 0, pl: 0 }}
                          >
                            {row.label}
                          </TableCell>
                        ) : null}
                        <TableCell
                          colSpan={row.label ? 1 : 2}
                          sx={{ border: 0, pr: 0, fontWeight: row.label ? 500 : 400 }}
                        >
                          {row.value}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No technical specifications listed yet.
                </Typography>
              )}

              {detailTool.safety_instructions ? (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Safety
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {detailTool.safety_instructions}
                  </Typography>
                </>
              ) : null}

              <Typography variant="h6" color="primary">
                €{(Number(detailTool.sell_price) || 0).toFixed(2)}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailTool(null)}>Close</Button>
              <Button
                variant="contained"
                startIcon={<CartIcon />}
                disabled={
                  (Number(detailTool.stock_quantity) || 0) <= 0 ||
                  (Number(detailTool.sell_price) || 0) <= 0
                }
                onClick={() => {
                  handleAdd(detailTool);
                  setDetailTool(null);
                }}
              >
                Add to cart
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {!embedded && <FloatingCart onCheckout={handleCheckout} />}
    </Box>
  );
};

export default ToolShopPage;
